import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { useForm, usePage } from '@inertiajs/react'
import { useEffect, useState, useCallback } from 'react'
import type { SharedProps } from '@/types/inertia'
import { IconSelect } from '@/components/ui/IconSelect'
import { SUNLIGHT_ICONS, DIFFICULTY_ICONS, STATUS_ICONS, LIGHT_LEVEL_ICONS } from '@/lib/icons'

interface PlantData {
  id: number | null
  name: string
  species_name: string | null
  pot_diameter_cm: number | null
  location_id: number | null
  watering_frequency_days: number | null
  watering_amount_ml: number | null
  sunlight_preference: string | null
  difficulty_level: string | null
  status: string | null
  acquired_at: string | null
  notes: string | null
}

interface Props {
  plant: PlantData
  locations: { id: number; name: string; light_level: string | null }[]
}

const sunlightOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'full_sun', label: 'Jasne bezpośrednie' },
  { value: 'indirect', label: 'Jasne rozproszone' },
  { value: 'partial_shade', label: 'Półcień' },
  { value: 'shade', label: 'Cień' },
]

const difficultyOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'easy', label: 'Łatwa' },
  { value: 'medium_difficulty', label: 'Średnia' },
  { value: 'hard', label: 'Trudna' },
]

const statusOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'healthy', label: 'Zdrowa' },
  { value: 'needs_attention', label: 'Wymaga uwagi' },
  { value: 'sick', label: 'Chora' },
  { value: 'dormant', label: 'Uśpiona' },
]

function PlantsNew({ plant, locations }: Props) {
  const { errors } = usePage<SharedProps>().props
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([])

  const { data, setData, post, processing } = useForm({
    plant: {
      name: plant.name || '',
      species_name: plant.species_name || '',
      pot_diameter_cm: plant.pot_diameter_cm || '',
      location_id: plant.location_id || '',
      watering_frequency_days: plant.watering_frequency_days || '',
      watering_amount_ml: plant.watering_amount_ml || '',
      sunlight_preference: plant.sunlight_preference || '',
      difficulty_level: plant.difficulty_level || '',
      status: plant.status || 'healthy',
      acquired_at: plant.acquired_at || new Date().toISOString().split('T')[0],
      notes: plant.notes || '',
      photos: [] as File[],
    },
  })

  const setPlantField = useCallback((field: string, value: unknown) => {
    setData('plant', { ...data.plant, [field]: value })
  }, [data.plant, setData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/plants', { forceFormData: true })
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setData('plant', { ...data.plant, photos: files })
    setPhotoPreviews(files.map((f) => URL.createObjectURL(f)))
  }

  // Fill form from Andrzej data — with field highlight animation
  const applyFormData = useCallback((formData: Record<string, unknown>) => {
    // Map all possible field names from Andrzej tools
    const fieldMap: Record<string, string> = {
      name: 'name', common_name: 'name',
      species_name: 'species_name',
      pot_diameter_cm: 'pot_diameter_cm', location_id: 'location_id',
      watering_frequency_days: 'watering_frequency_days',
      watering_amount_ml: 'watering_amount_ml',
      sunlight_preference: 'sunlight_preference',
      difficulty_level: 'difficulty_level',
      status: 'status',
      notes: 'notes', care_notes: 'notes',
    }

    const updates: Record<string, unknown> = {}
    const filledFields: string[] = []

    for (const [key, val] of Object.entries(formData)) {
      const field = fieldMap[key]
      if (!field || val === null || val === undefined || val === '') continue
      updates[field] = String(val)
      filledFields.push(field)
    }

    if (Object.keys(updates).length > 0) {
      setData((prev: typeof data) => ({ ...prev, plant: { ...prev.plant, ...updates } }))

      // Animate fields with staggered highlight
      let delay = 0
      for (const field of filledFields) {
        const inputId = `plant_${field}`
        setTimeout(() => {
          const el = document.getElementById(inputId)
          if (!el) return
          el.classList.add('andrzej-filling')
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => el.classList.remove('andrzej-filling'), 1500)
        }, delay)
        delay += 200
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setData])

  // Prefill from sessionStorage (set by Andrzej navigate_and_fill/fill_plant_form)
  useEffect(() => {
    const pending = sessionStorage.getItem('andrzej-fill-form')
    if (pending) {
      sessionStorage.removeItem('andrzej-fill-form')
      try {
        const formData = JSON.parse(pending)
        setTimeout(() => applyFormData(formData), 100)
      } catch { /* ignore */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Listen for real-time fill events (when already on this page)
  useEffect(() => {
    const handler = (e: CustomEvent) => applyFormData(e.detail)
    window.addEventListener('andrzej:fill-form', handler as EventListener)
    return () => window.removeEventListener('andrzej:fill-form', handler as EventListener)
  }, [applyFormData])

  useEffect(() => {
    return () => photoPreviews.forEach((url) => URL.revokeObjectURL(url))
  }, [photoPreviews])

  return (
    <AtlasPage>
      <PageTitle title="Nova Planta" subtitle="Dodaj nową roślinę do kolekcji" />
      <Separator />

      <form onSubmit={handleSubmit} className="space-y-md">
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <FormField label="Nazwa" error={errors?.name}>
            <input id="plant_name" type="text" value={data.plant.name} onChange={(e) => setPlantField('name', e.target.value)} className="form-input" required />
          </FormField>

          <FormField label="Gatunek" error={errors?.species_name}>
            <input id="plant_species_name" type="text" value={data.plant.species_name} onChange={(e) => setPlantField('species_name', e.target.value)} className="form-input" placeholder="np. Monstera deliciosa" />
          </FormField>

          <FormField label="Średnica doniczki (cm)">
            <input id="plant_pot_diameter_cm" type="number" value={data.plant.pot_diameter_cm} onChange={(e) => setPlantField('pot_diameter_cm', e.target.value)} className="form-input" min="1" step="0.1" />
          </FormField>

          <FormField label="Miejsce">
            <IconSelect
              id="plant_location_id"
              value={String(data.plant.location_id)}
              onChange={(v) => setPlantField('location_id', v)}
              options={locations.map((l) => ({ value: String(l.id), label: l.name, iconKey: l.light_level || '' }))}
              iconMap={LIGHT_LEVEL_ICONS}
              placeholder="— Brak —"
            />
          </FormField>

          <FormField label="Częstotliwość podlewania (dni)">
            <input id="plant_watering_frequency_days" type="number" value={data.plant.watering_frequency_days} onChange={(e) => setPlantField('watering_frequency_days', e.target.value)} className="form-input" min="1" />
          </FormField>

          <FormField label="Ilość wody (ml)">
            <input id="plant_watering_amount_ml" type="number" value={data.plant.watering_amount_ml} onChange={(e) => setPlantField('watering_amount_ml', e.target.value)} className="form-input" min="1" />
          </FormField>

          <FormField label="Nasłonecznienie">
            <IconSelect id="plant_sunlight_preference" value={data.plant.sunlight_preference} onChange={(v) => setPlantField('sunlight_preference', v)} options={sunlightOptions} iconMap={SUNLIGHT_ICONS} />
          </FormField>

          <FormField label="Trudność">
            <IconSelect id="plant_difficulty_level" value={data.plant.difficulty_level} onChange={(v) => setPlantField('difficulty_level', v)} options={difficultyOptions} iconMap={DIFFICULTY_ICONS} />
          </FormField>

          <FormField label="Status">
            <IconSelect id="plant_status" value={data.plant.status} onChange={(v) => setPlantField('status', v)} options={statusOptions} iconMap={STATUS_ICONS} />
          </FormField>

          <FormField label="Data nabycia">
            <input id="plant_acquired_at" type="date" value={data.plant.acquired_at} onChange={(e) => setPlantField('acquired_at', e.target.value)} className="form-input" />
          </FormField>
        </div>

        <FormField label="Notatki">
          <textarea id="plant_notes" value={data.plant.notes} onChange={(e) => setPlantField('notes', e.target.value)} className="form-input min-h-[6rem]" />
        </FormField>

        <FormField label="Zdjęcia">
          <input type="file" multiple accept="image/*" onChange={handlePhotoChange}
            className="block w-full font-label text-sm text-frame file:mr-sm file:border file:border-frame file:bg-paper-dark file:px-sm file:py-xs file:text-xs file:text-ink file:font-label" />
          {photoPreviews.length > 0 && (
            <div className="flex flex-wrap gap-sm mt-sm">
              {photoPreviews.map((url, i) => <img key={i} src={url} alt="" className="h-20 w-20 object-cover border border-frame/30" />)}
            </div>
          )}
        </FormField>

        <Separator />

        <div className="flex items-center justify-center gap-md">
          <Button type="submit" disabled={processing}>Dodaj roślinę</Button>
          <Button href="/plants" variant="secondary">Anuluj</Button>
        </div>
      </form>

      <PageNumber number={4} />
    </AtlasPage>
  )
}

function FormField({ label, error, children }: { label: string; error?: string[]; children: React.ReactNode }) {
  return (
    <div>
      <label className="block font-label text-xs text-frame uppercase tracking-wide mb-xs">{label}</label>
      {children}
      {error && <p className="text-xs text-danger mt-xs">{Array.isArray(error) ? error.join(', ') : error}</p>}
    </div>
  )
}

PlantsNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default PlantsNew
